import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import ResponsiveSection from "@/components/layout/ResponsiveSection";

import HeroSection from "@/components/layout/HeroSection";
import PageBreadcrumbs from "@/components/PageBreadcrumbs";
import { Shield, FileText, Scale, Activity, TrendingUp, Laptop, Award, CheckCircle2, AlertCircle, Phone } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import weitereVersicherungenHero from "@/assets/weitere-versicherungen-hero.jpg";
import { useImagePreload } from "@/hooks/useImagePreload";

const WeitereVersicherungen = () => {
  useImagePreload(weitereVersicherungenHero);
  const insurances = [
    {
      name: "Berufshaftpflicht",
      slug: "berufshaftpflicht",
      description: "Unverzichtbarer Schutz bei Behandlungsfehlern",
      category: "Pflicht",
      rating: "Pflichtversicherung",
      highlight: "Gesetzlich vorgeschrieben",
      icon: Shield,
      color: "destructive"
    },
    {
      name: "Betriebshaftpflicht",
      slug: "betriebshaftpflicht",
      description: "Absicherung bei Personen- und Sachschäden",
      category: "Pflicht",
      rating: "Pflichtversicherung",
      highlight: "Grundabsicherung",
      icon: Shield,
      color: "destructive"
    },
    {
      name: "Inhaltsversicherung",
      slug: "inhaltsversicherung",
      description: "Schutz für Praxiseinrichtung und medizinisches Gerät",
      category: "Wichtig",
      rating: "Empfohlen",
      highlight: "Bis zu 1,5 Mio. € Deckung",
      icon: FileText,
      color: "accent"
    },
    {
      name: "Rechtsschutzversicherung",
      slug: "rechtsschutz",
      description: "Rechtliche Unterstützung bei Streitigkeiten",
      category: "Wichtig",
      rating: "Empfohlen",
      highlight: "Anwalts- & Gerichtskosten",
      icon: Scale,
      color: "accent"
    },
    {
      name: "Praxisausfallversicherung",
      slug: "praxisausfall",
      description: "Finanzielle Absicherung bei Betriebsunterbrechung",
      category: "Ergänzend",
      rating: "Sinnvoll",
      highlight: "Umsatzausfall abgesichert",
      icon: Activity,
      color: "warning"
    },
    {
      name: "Elektronikversicherung",
      slug: "elektronik",
      description: "Spezieller Schutz für medizinische IT und Geräte",
      category: "Ergänzend",
      rating: "Sinnvoll",
      highlight: "Hochwertige Medizintechnik",
      icon: Laptop,
      color: "warning"
    }
  ];

  const pflichtInsurances = insurances.filter(i => i.category === "Pflicht");
  const wichtigInsurances = insurances.filter(i => i.category === "Wichtig");
  const ergaenzendInsurances = insurances.filter(i => i.category === "Ergänzend");

  const getCategoryColor = (category: string) => {
    switch(category) {
      case "Pflicht": return "bg-destructive/10 text-destructive border-destructive/30";
      case "Wichtig": return "bg-accent/10 text-accent border-accent/30";
      case "Ergänzend": return "bg-yellow-600/10 text-yellow-600 border-yellow-600/30";
      default: return "bg-muted text-foreground/70 border-border";
    }
  };

  return (
    <div className="min-h-screen relative">
      
      <div className="relative z-10">
        <Navigation />
        <PageBreadcrumbs items={[
          { label: "Home", path: "/" },
          { label: "Versicherungen" }
        ]} />

        {/* Hero Section */}
        <HeroSection
          image={weitereVersicherungenHero}
          alt="Weitere Versicherungen für Arztpraxen"
          title="Zusätzliche Versicherungen für medizinische Einrichtungen – Rundum-Schutz für Ihre Praxis"
          gradientPreset="none"
          imageOpacity={30}
          overlayColor="background"
          className="py-20 sm:py-24 lg:py-32 px-4"
        >
          <div className="container mx-auto max-w-4xl text-center relative z-10 animate-fade-in">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-accent/10 rounded-full mb-6">
              <Shield className="w-7 h-7 text-accent" />
            </div>
            
            <h1 className="heading-1 mb-6 text-foreground leading-tight">
              Wichtige Versicherungen für Arztpraxen
            </h1>
            
            <div className="mb-6">
              <span className="inline-block px-4 py-2 bg-accent/5 rounded-full text-accent text-sm font-medium">
                Absicherung • Schutz • Sicherheit
              </span>
            </div>
            
            <p className="body-text text-foreground/70 max-w-2xl mx-auto">
              Neben der Cyberversicherung gibt es weitere essenzielle Versicherungen, die jede Arztpraxis haben sollte
            </p>
          </div>
        </HeroSection>


      {/* Pflichtversicherungen */}
      <ResponsiveSection spacing="lg">
        <div className="flex items-center gap-3 mb-8">
          <AlertCircle className="w-6 h-6 text-destructive" />
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground break-words">Pflichtversicherungen</h2>
        </div>
        <p className="text-foreground/70 mb-8 max-w-3xl text-sm sm:text-base">
          Diese Versicherungen sind für Arztpraxen gesetzlich vorgeschrieben oder faktisch unverzichtbar.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {pflichtInsurances.map((insurance) => (
            <Card key={insurance.slug} className="border-2 border-destructive/30 hover:shadow-lg hover:border-destructive/50 transition-all">
              <CardHeader>
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="w-12 h-12 bg-destructive/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <insurance.icon className="w-6 h-6 text-destructive" />
                  </div>
                  <Badge variant="destructive" className="pointer-events-none">{insurance.rating}</Badge>
                </div>
                <CardTitle className="text-lg sm:text-xl break-words">{insurance.name}</CardTitle>
                <CardDescription className="text-sm sm:text-base">{insurance.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-foreground/70 mb-4">
                  <Award className="w-4 h-4 text-destructive" />
                  <span>{insurance.highlight}</span>
                </div>
                <Button variant="outline" className="w-full border-destructive/50 text-destructive hover:border-destructive hover:shadow-[0_0_15px_hsl(var(--destructive)/0.4)]" asChild>
                  <Link to={`/weitere-versicherungen/${insurance.slug}`}>
                    Mehr erfahren
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </ResponsiveSection>

      {/* Wichtige Absicherungen */}
      <ResponsiveSection variant="accent" spacing="lg">
        <div className="flex items-center gap-3 mb-8">
          <CheckCircle2 className="w-6 h-6 text-accent" />
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground break-words">Wichtige Absicherungen</h2>
        </div>
        <p className="text-foreground/70 mb-8 max-w-3xl text-sm sm:text-base">
          Dringend empfohlene Versicherungen zum Schutz Ihrer Praxis vor den häufigsten Risiken.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {wichtigInsurances.map((insurance) => (
            <Card key={insurance.slug} className="border-2 border-accent/30 hover:shadow-lg hover:border-accent/50 transition-all">
              <CardHeader>
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <insurance.icon className="w-6 h-6 text-accent" />
                  </div>
                  <Badge className="bg-accent/10 text-accent border-accent/30 pointer-events-none">{insurance.rating}</Badge>
                </div>
                <CardTitle className="text-lg sm:text-xl break-words">{insurance.name}</CardTitle>
                <CardDescription className="text-sm sm:text-base">{insurance.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-foreground/70 mb-4">
                  <Award className="w-4 h-4 text-accent" />
                  <span>{insurance.highlight}</span>
                </div>
                <Button variant="outline" className="w-full" asChild>
                  <Link to={`/weitere-versicherungen/${insurance.slug}`}>
                    Mehr erfahren
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </ResponsiveSection>

      {/* Ergänzende Versicherungen */}
      <ResponsiveSection spacing="lg">
        <div className="flex items-center gap-3 mb-8">
          <TrendingUp className="w-6 h-6 text-yellow-600" />
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground break-words">Ergänzende Versicherungen</h2>
        </div>
        <p className="text-foreground/70 mb-8 max-w-3xl text-sm sm:text-base">
          Sinnvolle Zusatzversicherungen für erweiterten Schutz Ihrer Praxis.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {ergaenzendInsurances.map((insurance) => (
            <Card key={insurance.slug} className="border-2 border-yellow-600/30 hover:shadow-lg hover:border-yellow-600/50 transition-all">
              <CardHeader>
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="w-12 h-12 bg-yellow-600/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <insurance.icon className="w-6 h-6 text-yellow-600" />
                  </div>
                  <Badge className="bg-yellow-600/10 text-yellow-600 border-yellow-600/30 pointer-events-none">{insurance.rating}</Badge>
                </div>
                <CardTitle className="text-lg sm:text-xl break-words">{insurance.name}</CardTitle>
                <CardDescription className="text-sm sm:text-base">{insurance.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-foreground/70 mb-4">
                  <Award className="w-4 h-4 text-yellow-600" />
                  <span>{insurance.highlight}</span>
                </div>
                <Button variant="outline" className="w-full border-yellow-600/50 text-yellow-600 hover:border-yellow-600 hover:shadow-[0_0_15px_rgba(202,138,4,0.4)]" asChild>
                  <Link to={`/weitere-versicherungen/${insurance.slug}`}>
                    Mehr erfahren
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </ResponsiveSection>

      <CTASection 
        title="Persönliche Beratung gewünscht?"
        subtitle={<>Lassen Sie sich kostenlos beraten,<br />welche Versicherungen für Ihre Praxis sinnvoll sind</>}
      />
      
      <Footer />
      </div>
    </div>
  );
};

export default WeitereVersicherungen;
